import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Alert,
  Link,
  useTheme,
  GlobalStyles,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import { RootState } from '../../reducers';
import { forgotPassword } from './authThunks';

const ForgotPassword: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { status } = useSelector((state: RootState) => ({
    status: state.auth.forgotPassword.status,
  }));
  const [email, setEmail] = useState<string>('');

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handleResetPassword = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    dispatch(forgotPassword(email));
  };

  const renderResponseMessage = () => {
    if (status === 'failed') {
      return (
        <Alert severity="error" id="forgot_password_error_alert" sx={{ mt: 2 }}>
          There seems to be an issue. Please verify that your email is correct.
        </Alert>
      );
    }
    if (status === 'succeeded') {
      return (
        <Alert
          severity="success"
          id="forgot_password_success_alert"
          sx={{ mt: 2 }}
        >
          Reset link sent to {email}
        </Alert>
      );
    }
    return null;
  };

  return (
    <>
      <GlobalStyles styles={{ body: { margin: 0 } }} />
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 0,
          m: 0,
          overflow: 'hidden',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <Box
          component="form"
          onSubmit={handleResetPassword}
          sx={{
            p: theme.spacing(8),
            borderRadius: theme.spacing(1),
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.secondary.main}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            maxWidth: 330,
          }}
        >
          <Typography variant="h4" sx={{ mb: 2 }}>
            Reset Password
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 3,
              textAlign: 'center',
              maxWidth: 340,
            }}
          >
            We&apos;ll send password reset instructions to your email address if
            an account exists.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
              width: '100%',
            }}
          >
            <TextField
              id="forgot-password-email"
              label="Email"
              variant="outlined"
              fullWidth
              margin="normal"
              value={email}
              onChange={handleEmailChange}
              aria-label="email"
              sx={{
                mb: 6,
                width: 320,
                height: 16,
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
                '& .MuiInputBase-input': {
                  '&:-webkit-autofill': {
                    WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.paper} inset`,
                    WebkitTextFillColor: theme.palette.text.primary,
                  },
                },
              }}
            />
          </Box>
          <LoadingButton
            id="forgot-password-button"
            variant="contained"
            tabIndex={-1}
            loading={status === 'pending'}
            loadingPosition="start"
            type="submit"
            disabled={!email}
            fullWidth
            sx={{ padding: theme.spacing(2), width: 320 }}
          >
            Send Reset Link
          </LoadingButton>

          <Link href="#/signin" sx={{ color: theme.palette.primary.main }}>
            <Typography
              id="forgot-password-link"
              sx={{
                fontSize: '0.8rem',
                mt: 3,
                color: theme.palette.primary.main,
                textTransform: 'none',
                textDecoration: 'underline',
                fontFamily: theme.typography.fontFamily,
              }}
            >
              Back to Sign In
            </Typography>
          </Link>

          {renderResponseMessage()}
        </Box>
      </Box>
    </>
  );
};

export default ForgotPassword;
